import { QuestionCircleOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import posthog from '@leyan/lytics';
import { useMemoizedFn, useMount, useSize } from 'ahooks';
import { Affix, Pagination, Radio, Space, Spin, Tooltip } from 'antd';
import Empty from 'components/Empty';
import useModal from 'hooks/useModal';
import React, { HTMLAttributes, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { ReturnOrderListItemV2, SecondaryProcessStatus } from 'services/api/refund';
import toLowerCase from 'utils/toLowerCase';
import Filter, { FilterChangeEvent } from '../components/OrderFilter/Filter';
import RefundDrawer from '../components/RefundDrawer';
import ReturnListItem from '../components/ReturnListItem';
import useFilter from '../hooks/useFilter';
import { usePlatform } from '../hooks/usePlatformList';
import useReturnList from '../hooks/useReturnList';
import LazadaReturnList from './LazadaReturnList';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { ReturnListTabs } from '../components/ReturnListTabs';
import { FilterIdType } from '../utils/constants';
import { ShopeeReturnFilter } from '../components/OrderFilter/ShopeeReturnFilter';
import { eventBus } from 'services/eventBus';
import { useIsLightChatLayout } from 'hooks/useChatLayoutType';
import { OrderDrawer } from 'components/LightConversation/components/OrderDrawer';

const Container = styled.div`
  flex: 1 0 0 !important;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  margin: 16px;

  .ant-tabs-nav {
    flex: 0 0 auto;
  }
  .ant-pagination {
    text-align: right;
  }

  > .ant-spin-nested-loading {
    flex: 1 0 0;
    padding: 0 16px 16px;

    > .ant-spin-container {
      height: 100%;
      display: flex;
      flex-direction: column;
      > ul {
        flex: 0 0 auto;
        z-index: 3;
      }
    }
  }
`;

export const PaginationUI = styled(Pagination)`
  position: sticky;
  bottom: 0;
  margin: 0 0 -16px;
  padding: 16px 0;
  background: #fff;
  .ant-pagination-jump-next + .ant-pagination-item {
    display: none;
  }
`;

const ReturnList = React.memo(() => {
  const platform = usePlatform()!;
  const { t } = useTranslate();
  const { query, onQueryChange } = useFilter();
  const {
    list = [],
    statusFilter,
    setStatusFilter,
    loading,
    statusCount,
    pagination: { pageSize, current, onChange, total },
    refresh,
    conversationList = [],
    secondaryProcessStatus,
    setSecondaryProcessStatus,
  } = useReturnList(query, platform);

  const isLightLayout = useIsLightChatLayout();

  const { update, meta, visible, hide } = useModal(false);
  // const [, forceUpdate] = useState(0);
  const modalMetaRef = useRef<{
    row: ReturnOrderListItemV2;
    conversation?: Conversation.Conversation;
    order?: Conversation.Trade.Vo;
  } | null>(null);

  const handleFilterChange = useMemoizedFn((e: FilterChangeEvent) => {
    const { snType, sn } = e;
    const { orderSn, returnSn, returnTrackingNumber } = query;
    if (
      (snType === FilterIdType.orderId && sn !== orderSn) ||
      (snType === FilterIdType.returnId && sn !== returnSn) ||
      (snType === FilterIdType.returnTrackingNumber && sn !== returnTrackingNumber)
    ) {
      setStatusFilter('ALL');
    }

    onQueryChange(e);
  });

  const showDueTimeEnd = useMemo(
    () => platform === 'KWAI' && statusFilter === 'PENDING',
    [platform, statusFilter],
  );

  const filterComponent = useMemo(() => {
    if (['SHOPEE', 'LAZADA', 'TIKTOK', 'TIKTOK_US'].includes(platform)) {
      return <ShopeeReturnFilter filterType="return" onChange={handleFilterChange} />;
    }
    return (
      <Filter onChange={handleFilterChange} filterType="return" showDueTimeEnd={showDueTimeEnd} />
    );
  }, [handleFilterChange, platform, showDueTimeEnd]);

  useEffect(() => {
    if (visible && modalMetaRef.current?.row) {
      const {
        current: { row },
      } = modalMetaRef;
      const nextRow = list.find((it) => it.orderSn === row.orderSn);
      modalMetaRef.current.row = nextRow || row;
    }

    eventBus.emit(
      'RETURN_LIST_ID_SYNC',
      list.reduce((prev, cur) => {
        if (cur.internalReturnId) {
          prev.push(cur.internalReturnId);
        }
        return prev;
      }, [] as string[]),
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  useMount(() => {
    posthog.capture('$returnOrder', {
      type: 'entryPage',
    });
  });

  const firstLoad = useRef(false);
  if (!firstLoad.current && statusCount?.pendingCount === 0) {
    firstLoad.current = true;
    setStatusFilter('ALL');
  }

  let detailDrawer = null;
  if (modalMetaRef.current?.row) {
    const { row, conversation } = modalMetaRef.current;
    if (isLightLayout && conversation) {
      detailDrawer = (
        <OrderDrawer
          visible={visible}
          conversation={conversation}
          sendable={false}
          entryType="refund"
          returnOrder={row}
          defaultActiveTab="refundDetail"
          onClose={() => hide()}
          contactEnable
        />
      );
    } else {
      detailDrawer = (
        <RefundDrawer
          platform={toLowerCase(platform)}
          visible={visible}
          onUpdate={refresh}
          key={`${meta}`}
          onClose={() => hide()}
          type="refund"
          row={row}
          conversation={conversation}
          refreshList={refresh}
        />
      );
    }
  }

  return (
    <>
      {filterComponent}
      <Container className="list">
        <Affix target={() => document.querySelector<HTMLDivElement>('.refund-container')}>
          <div className="bg-fill-0">
            <ReturnListTabs
              query={query}
              statusFilter={statusFilter}
              secondaryProcessStatus={secondaryProcessStatus}
              onChange={(value) => {
                setStatusFilter(value);
                setSecondaryProcessStatus('ALL' as any);
              }}
              statusCount={statusCount}
            />
            {platform === 'SHOPEE' && statusFilter === 'PENDING' ? (
              <div className="px-4 pb-4">
                <Radio.Group
                  value={secondaryProcessStatus}
                  onChange={(e) => setSecondaryProcessStatus(e.target.value)}
                >
                  {Object.values(SecondaryProcessStatus).map((status) => (
                    <Radio.Button value={status}>
                      {t(`shopee_refund_secondary_process_status_${status}`)}
                      <Tooltip title={t(`shopee_refund_secondary_process_status_tips_${status}`)}>
                        <QuestionCircleOutlined className="ml-1" />
                      </Tooltip>
                    </Radio.Button>
                  ))}
                </Radio.Group>
              </div>
            ) : null}
            {isTiKTokUpperCase(platform) && statusFilter === 'PENDING' && (
              <div className="px-4 pb-4">
                <Radio.Group
                  value={secondaryProcessStatus}
                  onChange={(e) => setSecondaryProcessStatus(e.target.value)}
                >
                  {[
                    { value: 'ALL', label: t('全部') },
                    { value: 'ONLY_REFUND_APPLIED', label: t('买家申请仅退款') },
                    { value: 'RETURN_APPLIED', label: t('买家申请退货退款') },
                    { value: 'EXCHANGE_APPLIED', label: t('买家申请换货') },
                  ].map(({ value, label }) => (
                    <Radio.Button value={value}>{label}</Radio.Button>
                  ))}
                </Radio.Group>
              </div>
            )}
            {isTiKTokUpperCase(platform) && statusFilter === 'PROCESS' && (
              <div className="px-4 pb-4">
                <Radio.Group
                  value={secondaryProcessStatus}
                  onChange={(e) => setSecondaryProcessStatus(e.target.value)}
                >
                  {[
                    { value: 'ALL', label: t('全部') },
                    { value: 'WAITING_RETURN', label: t('tiktok.3') },
                    { value: 'BUYER_HAS_SHIPPED', label: t('tiktok.4') },
                    { value: 'WAITING_PLATFORM_AUDIT', label: t('平台审核中') },
                  ].map(({ value, label }) => (
                    <Radio.Button value={value}>{label}</Radio.Button>
                  ))}
                </Radio.Group>
              </div>
            )}
          </div>
        </Affix>

        <Spin spinning={loading}>
          <Table platform={platform}>
            <ul>
              <li className="td-title">{t('tableColumn.item')}</li>
              <li className="td-amount">{t('tableColumn.price')}</li>
              <li className="td-reason">{t('tableColumn.returnReason')}</li>
              {platform === 'SHOPEE' && <li className="td-rqReason">{t('复核后退货退款原因')}</li>}
              {isTiKTokUpperCase(platform) && (
                <li className="td-amount">{t('translation.订单状态')}</li>
              )}
              <li className="td-status">{t('tableColumn.status')}</li>
              {platform === 'SHOPEE' && [<li>{t('正向物流')}</li>, <li>{t('逆向物流')}</li>]}
              <li className="td-action">
                <Space>{t('操作')}</Space>
              </li>
            </ul>
            {!list?.length && !loading ? (
              <Empty />
            ) : (
              list?.map((row, index) => (
                <ReturnListItem
                  row={row}
                  key={row.returnSn + String(index) + platform}
                  refreshList={refresh}
                  conversation={conversationList[index]}
                  onClick={async () => {
                    modalMetaRef.current = { row, conversation: conversationList[index] };
                    update(true, `${row.orderSn}-${index}`);
                  }}
                />
              ))
            )}
          </Table>
          <PaginationUI
            className="!mt-4"
            pageSize={pageSize}
            current={current}
            onChange={onChange}
            total={total}
            showSizeChanger
            pageSizeOptions={['10', '20', '100']}
          />
        </Spin>
        {detailDrawer}
      </Container>
    </>
  );
});

const ReturnListWrap = () => {
  const platform = usePlatform()!;

  if (platform === 'LAZADA') {
    return <LazadaReturnList key="lazadaReturnList" />;
  }

  return <ReturnList key="otherReturnList" />;
};

type Shadow = 'none' | 'left' | 'right' | 'both';

const widthList = [330, 160, 240, 140, 180, 190, 270, 190];

function Table(props: { platform: General.PlatformUpperCase } & HTMLAttributes<HTMLDivElement>) {
  const { platform, ...otherProps } = props;

  const ref = useRef<HTMLDivElement>(null);

  const { width = 0 } = useSize(ref) || {};

  const [shadow, setShadow] = useState<Shadow>('none');

  const _widthList = useMemo(() => {
    return platform === 'SHOPEE' ? widthList : [...widthList.slice(0, 5), widthList[7]];
  }, [platform]);

  useEffect(() => {
    const minWidth = _widthList.reduce((res, cur) => res + cur, 0);

    if (width >= minWidth) {
      setShadow('none');
      return;
    }

    const target = ref.current;

    if (!target) {
      return;
    }

    const handler = () => {
      const { clientWidth, scrollWidth, scrollLeft } = target;

      setShadow(
        scrollLeft <= 0 ? 'right' : clientWidth + scrollLeft >= scrollWidth ? 'left' : 'both',
      );
    };

    handler();

    target.addEventListener('scroll', handler);

    return () => target.removeEventListener('scroll', handler);
  }, [width, _widthList]);

  return <TableContainer ref={ref} shadow={shadow} widthList={_widthList} {...otherProps} />;
}

const TableContainer = styled.div<{
  shadow: Shadow;
  widthList: number[];
}>`
  width: 100%;
  padding-bottom: 1px;
  overflow-x: auto;
  > ul {
    display: flex;
    align-items: stretch;
    margin: 0;
    padding: 0;
    list-style: none;
    > li {
      flex: 0 0 auto;
      padding: 0 12px;
      background: #fff;
    }
    ${({ widthList }) => {
      const width = widthList.reduce((res, cur) => res + cur, 0);
      return widthList
        .map((cur, index) => {
          return `
          > li:nth-of-type(${index + 1}) {
            width: ${(cur / width) * 100}%;
            min-width: ${cur}px;
          }
        `;
        })
        .join('');
    }}
    > li:first-of-type,
    > li:last-of-type {
      position: sticky;
      z-index: 2;
      &::after {
        position: absolute;
        top: 0;
        bottom: -1px;
        width: 30px;
        transition: box-shadow 0.3s;
        content: '';
        pointer-events: none;
      }
    }
    > li:first-of-type {
      left: 0;
      &::after {
        display: ${({ shadow }) => (shadow === 'left' || shadow === 'both' ? 'block' : 'none')};
        right: 0;
        transform: translate(100%);
        box-shadow: inset 10px 0 8px -8px #00000026;
      }
    }
    > li:last-of-type {
      right: 0;
      &::after {
        display: ${({ shadow }) => (shadow === 'right' || shadow === 'both' ? 'block' : 'none')};
        left: 0;
        transform: translate(-100%);
        box-shadow: inset -10px 0 8px -8px #00000026;
      }
    }
    &:first-of-type {
      > li {
        display: flex;
        align-items: center;
        height: 56px;
        color: #232426;
        font-weight: 500;
        text-align: left;
        background: #f7f9fa;
        white-space: pre-wrap;
      }
    }
    &:nth-child(2n) {
      position: sticky;
      left: 0;
      width: 100%;
      > li {
        width: 100%;
        background: #f2f8ff;
      }
    }
  }
`;

export default ReturnListWrap;
